import { fetchDaily, localDate } from "./daily.ts";
import type { About } from "./game/about.ts";
import { GeoData, type Country } from "./game/geo.ts";
import { status } from "./game/rules.ts";
import { PAGE_DATA_ID, type EmbeddedData, type PageData } from "./page.ts";
import { loadGuesses, safeStorage } from "./store.ts";
import { Globe } from "./ui/globe.ts";
import { showLanding } from "./ui/landing.ts";
import { showNoDaily } from "./ui/no-daily.ts";
import { savedGuesses, showPlay } from "./ui/play.ts";
import { showResult } from "./ui/result.ts";

const root = document.getElementById("app")!;
const embedded = JSON.parse(document.getElementById(PAGE_DATA_ID)!.textContent ?? "{}") as EmbeddedData;
// The game's address: this script is at …/web/app.js.
const game = new URL("../", import.meta.url);
const reducedMotion = matchMedia("(prefers-reduced-motion: reduce)").matches;
const storage = safeStorage();
// The map data sits beside this script (…/web/app.js), wherever the game is served from.
const json = async <T>(path: string): Promise<T> => {
  const res = await fetch(new URL(path, import.meta.url));
  if (!res.ok) throw new Error(`${path}: ${res.status}`);
  return res.json() as Promise<T>;
};

const load = () => Promise.all([
  json<Country[]>("geo/countries.json"),
  json<{ ids: string[]; km: number[][] }>("geo/distances.json"),
  json<Record<string, number[][][][]>>("geo/shapes-web.json"),
]);
// The About text is a nicety: if it can't load, the result shows the fallback line.
const aboutLoading = json<Record<string, About>>("geo/about.json").catch((): Record<string, About> => ({}));
// Start loading now; a failure is retried when the player taps start.
let loading = load();
loading.catch(() => {});

const finish = (data: PageData, geo: GeoData) => async (guesses: string[], fresh: boolean): Promise<void> =>
  showResult(root, { data, geo, guesses, reducedMotion, fresh, about: await aboutLoading });

const landing = (data: PageData) => showLanding(root, data, async () => {
  const [countries, distances, shapes] = await loading.catch(() => (loading = load()));
  const geo = new GeoData(countries, distances);
  showPlay(root, {
    data, geo, storage, reducedMotion,
    makeGlobe: (host, onPick) => new Globe(host, shapes, geo, onPick, reducedMotion),
  }, finish(data, geo));
});

/** A reload of a finished game opens on the result (no sheet); anything else starts at the landing. */
async function open(data: PageData): Promise<void> {
  if (loadGuesses(storage, data.puzzle.number).length > 0) {
    try {
      const [countries, distances] = await loading;
      const geo = new GeoData(countries, distances);
      const guesses = savedGuesses(storage, data.puzzle.number, data.puzzle.answer, geo);
      if (status(data.puzzle.answer, guesses) !== "playing") { await finish(data, geo)(guesses, false); return; }
    } catch {
      // The data didn't load: the landing shows, and its start button retries.
    }
  }
  landing(data);
}

/** A shared link carries its puzzle; the top of the site plays the visitor's own date. */
async function start(): Promise<void> {
  if (embedded.puzzle) return open({ ...embedded, puzzle: embedded.puzzle });
  let puzzle;
  try {
    puzzle = await fetchDaily(localDate(), game);
  } catch {
    showNoDaily(root, embedded, () => void start());
    return;
  }
  if (!puzzle) { showNoDaily(root, embedded); return; }
  return open({ ...embedded, puzzle });
}
void start();
